import { useState, useMemo } from 'react';
import { Search, X, Check, Mail, Sparkles, User } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useDrivers } from '../../../hooks/useDrivers';
import { cn } from '../../../utils/cn';
import type { BookingRecord } from '../../../types';
import { formatBookingDate, formatBookingTime } from '../../../utils/format';
// import { sendDriverAssignmentEmail } from '../../../api/bookings/driverAssignmentApi';
import { generateMeetingBoardUrl } from '../../../api/bookings/driverAssignmentApi';
type AssignDriverModalProps = {
  isOpen: boolean;
  onClose: () => void;
  booking: BookingRecord | null;
  onAssign: (
    driverIds: string[],
    amounts?: Record<string, string>,
    meetingBoardUrl?: string,
    driverName?: string,
    includeDriverPhone?: boolean
  ) => Promise<boolean>;
};

export const AssignDriverModal = ({ isOpen, onClose, booking, onAssign }: AssignDriverModalProps) => {
  const { records: drivers } = useDrivers();
  const [step, setStep] = useState<1 | 2>(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDriverIds, setSelectedDriverIds] = useState<string[]>([]);
  const [amounts, setAmounts] = useState<Record<string, string>>({});
  const [isAssigning, setIsAssigning] = useState(false);
  const [includeDriverPhone, setIncludeDriverPhone] = useState(false);
  const filteredDrivers = useMemo(() => {
    if (!searchQuery.trim()) return drivers;
    const lowerQuery = searchQuery.toLowerCase();
    return drivers.filter(d => 
      d.name.toLowerCase().includes(lowerQuery) || 
      d.code.toLowerCase().includes(lowerQuery) ||
      d.vehicle.category.toLowerCase().includes(lowerQuery)
    );
  }, [drivers, searchQuery]);

  const toggleDriver = (id: string) => {
    setSelectedDriverIds(prev => 
      prev.includes(id) ? prev.filter(d => d !== id) : [...prev, id]
    );
  };

  const handleClose = () => {
    setStep(1);
    setSelectedDriverIds([]);
    setIncludeDriverPhone(false);
    setAmounts({});
    setSearchQuery('');
    onClose();
  };

  const handleConfirmAssign = async () => {
    if (!booking || selectedDriverIds.length === 0) return;
    setIsAssigning(true);

    try {
      const meetingBoardUrl = generateMeetingBoardUrl({
        reference: booking.reference,
        customerName: booking.customer.name,
        pickupLocation: booking.route.from,
        dropoffLocation: booking.route.to,
        scheduledAt: booking.scheduledAt,
        // flightNumber: 'OS742',
        vehicle:
          drivers.find(d => d.id === selectedDriverIds[0])?.vehicle.category ||
          'Saloon',
      });

      const assignedDriverNames = selectedDriverIds
        .map(id => drivers.find(d => d.id === id)?.name)
        .filter(Boolean)
        .join(', ');

      const success = await onAssign(
        selectedDriverIds,
        amounts,
        meetingBoardUrl,
        assignedDriverNames,
        includeDriverPhone
      )

      if (success !== true) {
        toast.error('Failed to assign driver. Please try again.')
        return
      }

      toast.success(
        `Driver assigned! ${assignedDriverNames} will receive the VIP Meeting Board access link.`,
        { duration: 5500, icon: '📧' }
      )

      handleClose()
    } catch (err) {
      console.error('Driver assignment error:', err);
      toast.error('Failed to prepare driver assignment.');
    } finally {
      setIsAssigning(false);
    }
  };

  if (!isOpen || !booking) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40">
      <div className={cn(
        "relative rounded-[16px] bg-[#F8F9FA] p-[24px] shadow-lg flex flex-col transition-all duration-300",
        step === 1 ? "w-full max-w-[480px]" : "w-full max-w-[850px]"
      )}>
        {/* Header (Shared) */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-host text-[18px] font-semibold text-black">
            {step === 1 ? 'Search Driver' : ''}
          </h2>
          <button 
            onClick={handleClose}
            className="flex h-[24px] w-[24px] items-center justify-center rounded-full bg-black text-white hover:bg-black/80 transition-colors ml-auto"
          >
            <X size={14} />
          </button>
        </div>

        {step === 1 ? (
          <>
            {/* Search */}
            <div className="relative mb-6">
              <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-black" />
              <input
                type="text"
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-[48px] w-full rounded-[8px] bg-white border border-[#E0E0E0] pl-[44px] pr-[16px] font-host text-[15px] font-medium text-black placeholder:text-[#1A1A1A] focus:outline-none"
              />
            </div>

            {/* Drivers List */}
            <h3 className="font-host text-[15px] font-medium text-black mb-3">Select Drivers</h3>
            <div className="flex flex-col gap-2 max-h-[300px] overflow-y-auto mb-6 pr-1">
              {filteredDrivers.map(driver => {
                const isSelected = selectedDriverIds.includes(driver.id);
                return (
                  <label
                    key={driver.id}
                    className={cn(
                      "flex cursor-pointer items-center justify-between rounded-[12px] p-3 transition-colors",
                      isSelected ? "bg-[#E6E6E6]" : "bg-transparent hover:bg-white"
                    )}
                  >
                    <div className="flex items-center gap-4">
                      <div className={cn(
                        "relative flex items-center justify-center w-[20px] h-[20px] rounded border shrink-0",
                        isSelected ? "bg-black border-black" : "border-black"
                      )}>
                        {isSelected && <Check size={14} className="text-white" />}
                        <input
                          type="checkbox"
                          className="hidden"
                          checked={isSelected}
                          onChange={() => toggleDriver(driver.id)}
                        />
                      </div>
                      {driver.avatar && !driver.avatar.includes('driver-avatar.png') ? (
                        <img
                          src={driver.avatar}
                          alt={driver.name}
                          className="h-[40px] w-[40px] rounded-full object-cover shrink-0"
                        />
                      ) : (
                        <div className="flex h-[40px] w-[40px] items-center justify-center rounded-full bg-[#EAEAEA] border border-[#DDDDDD] text-[#666666] shrink-0">
                          <User size={20} className="text-[#666666]" />
                        </div>
                      )}
                      <div className="flex flex-col">
                        <span className="font-host text-[12px] text-black leading-tight">{driver.vehicle.plate || 'AT-548796'}</span>
                        <span className="font-host text-[15px] font-semibold text-black leading-tight">{driver.name}</span>
                        <span className="font-host text-[12px] text-[#757575] leading-tight">{driver.vehicle.brand} {driver.vehicle.category}</span>
                        {driver.user_email && (
                          <span className="font-host text-[11px] text-blue-600 leading-tight mt-0.5 font-medium">
                            {driver.user_email}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex flex-col items-end gap-[6px]">
                      <span className="font-host text-[13px] text-[#719642]">{driver.code}</span>
                      <span className="flex items-center gap-1.5 font-host text-[12px] font-medium text-black">
                        <div className={cn("h-2 w-2 rounded-full", driver.availability === 'available' ? "bg-[#719642]" : "bg-red-500")} />
                        {driver.availability === 'available' ? 'Available' : 'Unavailable'}
                      </span>
                    </div>
                  </label>
                );
              })}
            </div>

            {/* Actions */}
            <div className="flex gap-4 mt-auto">
              <button
                onClick={handleClose}
                className="flex-1 h-[48px] rounded-[8px] bg-[#FFC5A6] font-host text-[16px] font-semibold text-black transition-colors hover:opacity-90"
              >
                Cancel
              </button>
              <button
                onClick={() => setStep(2)}
                disabled={selectedDriverIds.length === 0}
                className="flex-1 h-[48px] rounded-[8px] bg-[#B5D57B] font-host text-[16px] font-semibold text-black transition-colors hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </>
        ) : (
          <div className="flex flex-col md:flex-row gap-6 w-full">
            {/* Left: Trip Details */}
            <div className="flex-1 rounded-[16px] bg-white p-6 shadow-sm border border-[#E0E0E0]">
              <div className="flex items-start justify-between mb-8">
                <h3 className="font-host text-[18px] font-bold text-black">Trip Details</h3>
                <div className="bg-[#6B8DFF] rounded-[8px] px-3 py-1.5 flex flex-col items-end">
                  <span className="font-host text-[11px] text-white/80">Booking ID</span>
                  <span className="font-host text-[13px] font-medium text-white">{booking.reference}</span>
                </div>
              </div>

              <div className="flex flex-col gap-4">
                <div className="flex justify-between items-center">
                  <span className="font-host text-[14px] text-[#757575]">Trip Type</span>
                  <span className="font-host text-[14px] font-medium text-black">{booking.service}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="font-host text-[14px] text-[#757575]">Pickup Location</span>
                  <span className="font-host text-[14px] font-medium text-black">{booking.route.from}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="font-host text-[14px] text-[#757575]">Drop-off Location</span>
                  <span className="font-host text-[14px] font-medium text-black flex items-center gap-1.5">
                    {booking.route.to}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="font-host text-[14px] text-[#757575]">Pickup Date</span>
                  <span className="font-host text-[14px] font-medium text-black">{formatBookingDate(booking.scheduledAt)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="font-host text-[14px] text-[#757575]">Pickup Time</span>
                  <span className="font-host text-[14px] font-medium text-black">{formatBookingTime(booking.scheduledAt)}</span>
                </div>
                <div className="flex justify-between items-center mt-2">
                  <span className="font-host text-[14px] text-[#757575]">Estimated Distance</span>
                  <span className="font-host text-[14px] font-medium text-black">22 Km</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="font-host text-[14px] text-[#757575]">Estimated Duration</span>
                  <span className="font-host text-[14px] font-medium text-black">35 minutes</span>
                </div>
              </div>
            </div>

            {/* Right: Selected Drivers & Amount */}
            <div className="flex-1 flex flex-col">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-host text-[16px] font-medium text-black">Selected Driver</h3>
                <span className="text-[11px] font-semibold text-[#FF6A1A] flex items-center gap-1">
                  <Sparkles size={13} /> Auto-Email Enabled
                </span>
              </div>

              {/* Informative Auto Email Dispatch Banner */}
              <div className="rounded-[10px] bg-amber-50 border border-amber-200 p-2.5 text-[12px] text-amber-900 flex items-start gap-2 mb-3">
                <Mail size={16} className="text-[#FF6A1A] shrink-0 mt-0.5" />
                <div className="leading-tight">
                  <strong>Instant Driver Notification:</strong> Assigning this driver will automatically email them the full booking specs, agreed payout, and a direct link to their public <strong>VIP Meeting Board</strong>.
                </div>
              </div>

              <div className="flex flex-col gap-4 max-h-[340px] overflow-y-auto pr-2 pb-4">
                {selectedDriverIds.map(driverId => {
                  const driver = drivers.find(d => d.id === driverId);
                  if (!driver) return null;
                  return (
                    <div key={driver.id} className="rounded-[12px] bg-white border border-[#E0E0E0] p-4 flex flex-col gap-4">
                      <div className="flex items-start justify-between">
                        <div className="flex gap-4">
                          <div className="h-[60px] w-[60px] rounded-[8px] bg-[#EBEBEB] flex items-center justify-center shrink-0 overflow-hidden">
                            {driver.avatar && !driver.avatar.includes('driver-avatar.png') ? (
                              <img src={driver.avatar} alt={driver.name} className="h-full w-full rounded-[8px] object-cover" />
                            ) : (
                              <User size={28} className="text-[#757575]" />
                            )}
                          </div>
                          <div className="flex flex-col">
                            <span className="font-host text-[12px] text-black leading-tight">{driver.vehicle.plate || 'AT-548796'}</span>
                            <span className="font-host text-[15px] font-bold text-black leading-tight">{driver.name}</span>
                            <span className="font-host text-[12px] text-[#757575] leading-tight">{driver.vehicle.brand} {driver.vehicle.category}</span>
                            <span className="font-host text-[11px] text-blue-600 font-medium leading-tight mt-0.5">
                              📧 {driver.user_email}
                            </span>
                            <span className="font-host text-[12px] text-black flex items-center gap-1 mt-0.5">
                              📞 {driver.phone}
                            </span>
                          </div>
                        </div>
                        <span className="px-3 py-1 rounded-full border border-[#719642] text-[#719642] font-host text-[12px] font-medium bg-[#719642]/10">
                          {driver.availability === 'available' ? 'Available' : 'Unavailable'}
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        <span className="font-host text-[14px] font-medium text-black shrink-0">Driver Payout (€)</span>
                        <div className="relative flex-1">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 font-host text-[14px] font-medium text-black">€</span>
                          <input 
                            type="number"
                            value={amounts[driver.id] || ''}
                            onChange={(e) => setAmounts(prev => ({ ...prev, [driver.id]: e.target.value }))}
                            className="w-full h-[40px] rounded-[6px] border border-[#E0E0E0] pl-[24px] pr-[12px] font-host text-[14px] text-black focus:outline-none focus:border-black"
                            placeholder={String(booking.amountEur || 0)}
                          />
                        </div>
                      </div>
                      <label className="flex items-center gap-2.5 mb-3 cursor-pointer text-[13px] font-medium text-black">
                        <input
                          type="checkbox"
                          checked={includeDriverPhone}
                          onChange={(e) => setIncludeDriverPhone(e.target.checked)}
                          className="h-4 w-4 accent-black cursor-pointer"
                        />
                        <span>Include driver's phone in customer email</span>
                      </label>
                    </div>
                  );
                })}
              </div>

              {/* Actions Step 2 */}
              <div className="flex gap-4 mt-auto pt-4 justify-end">
                <button
                  disabled={isAssigning}
                  onClick={handleClose}
                  className="w-[110px] h-[48px] rounded-[8px] border border-[#E0E0E0] bg-white font-host text-[15px] font-semibold text-black transition-colors hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  disabled={isAssigning || selectedDriverIds.length === 0}
                  onClick={handleConfirmAssign}
                  className="px-6 h-[48px] rounded-[8px] bg-[#4A332C] font-host text-[15px] font-semibold text-white transition-colors hover:opacity-90 disabled:opacity-50 flex items-center gap-2 shadow-sm"
                >
                  {isAssigning ? (
                    <>
                      <div className="h-4 w-4 border-2 border-white/40 border-t-white animate-spin rounded-full" />
                      <span>Sending Assignment & Email...</span>
                    </>
                  ) : (
                    <>
                      <Mail size={16} />
                      <span>Assign Driver & Send Email</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
